/*
  Ivy Pillow — kaart-navbar: paneelgedrag, logowissel bij scrollen,
  megamenu en entrance-animatie. Gedrag en timing van het aangeleverde
  component behouden; respecteert prefers-reduced-motion.
*/
(() => {
  function initPaneel(nav) {
    const toggle = nav.querySelector('.navbar__toggle');
    const panel = nav.querySelector('.navbar__panel');
    if (!toggle || !panel) return;

    const setOpen = (open) => {
      nav.dataset.open = String(open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Menu sluiten' : 'Menu openen');
      // `inert` maakt het panel oninteractief en onzichtbaar voor hulptechnologie
      // zolang het dicht is, maar houdt het gerenderd voor de hoogte-animatie.
      panel.toggleAttribute('inert', !open);
      updateTransparant(nav);
    };

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      setOpen(nav.dataset.open !== 'true');
    });

    panel.querySelectorAll('.navbar__panel-link, .navbar__panel-cta').forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('click', (e) => {
      if (nav.dataset.open !== 'true') return;
      if (!nav.contains(e.target)) return setOpen(false);
      if (e.target.classList.contains('navbar__backdrop')) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.dataset.open === 'true') setOpen(false);
    });

    const mq = window.matchMedia('(min-width: 992px)');
    mq.addEventListener('change', () => {
      if (mq.matches) setOpen(false);
    });
  }

  // Transparant over de hero: alleen op de homepage (overlay), bovenaan de
  // pagina en zolang er geen menu of megamenu openstaat.
  function updateTransparant(nav) {
    const wrap = nav.closest('.navbar-wrap');
    const overlay = wrap && wrap.classList.contains('navbar-wrap--overlay');
    const transparant =
      overlay &&
      !nav.classList.contains('navbar--gescrold') &&
      nav.dataset.open !== 'true' &&
      !nav.classList.contains('navbar--mega-open');
    nav.classList.toggle('navbar--transparant', transparant);
  }

  // Scrollgedrag: de header blijft sticky staan en morft bij scrollen van
  // transparante kaart in de hero naar een witte fullwidth-balk (CSS doet de
  // animatie; hier wisselen alleen de klassen, met een kleine hysterese).
  function initScrollGedrag(nav) {
    const sectie = nav.closest('.section-ivy-navbar');
    let bezig = false;

    const zetStaat = () => {
      const y = window.scrollY;
      const was = nav.classList.contains('navbar--gescrold');
      const gescrold = was ? y > 24 : y > 64;
      nav.classList.toggle('navbar--gescrold', gescrold);
      if (sectie) sectie.classList.toggle('section-ivy-navbar--gescrold', gescrold);
      updateTransparant(nav);
      bezig = false;
    };

    const bijScroll = () => {
      if (bezig) return;
      bezig = true;
      requestAnimationFrame(zetStaat);
    };

    zetStaat();
    window.addEventListener('scroll', bijScroll, { passive: true });
    window.addEventListener('resize', zetStaat);
  }

  // Megamenu: knop met aria-expanded, opent op hover (desktop) en klik,
  // sluit met Escape, klik buiten en bij verlaten van de kaart.
  function initMega(nav) {
    const knop = nav.querySelector('.navbar__megaknop');
    const panel = nav.querySelector('.navbar__megapanel');
    if (!knop || !panel) return;

    const desktop = window.matchMedia('(min-width: 992px)');
    let sluitTimer = null;

    const setMega = (open) => {
      nav.classList.toggle('navbar--mega-open', open);
      knop.setAttribute('aria-expanded', String(open));
      panel.toggleAttribute('inert', !open);
      updateTransparant(nav);
    };

    knop.addEventListener('click', () => {
      setMega(!nav.classList.contains('navbar--mega-open'));
    });

    const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const megaGroep = knop.closest('li') || knop;
    nav.addEventListener('mouseenter', () => clearTimeout(sluitTimer), true);
    megaGroep.addEventListener('mouseenter', () => {
      if (desktop.matches && hover.matches) setMega(true);
    });
    nav.addEventListener('mouseleave', () => {
      if (!desktop.matches || !hover.matches) return;
      sluitTimer = setTimeout(() => setMega(false), 200);
    });

    // Hover op een ander menu-item, het merk of de iconen: megamenu netjes dicht
    const megaItem = megaGroep;
    const overige = nav.querySelectorAll('.navbar__menu > li, .navbar__brand, .navbar__actions');
    overige.forEach((el) => {
      if (el === megaItem) return;
      el.addEventListener('mouseenter', () => {
        if (!desktop.matches || !hover.matches) return;
        if (nav.classList.contains('navbar--mega-open')) setMega(false);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('navbar--mega-open')) {
        setMega(false);
        knop.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!nav.classList.contains('navbar--mega-open')) return;
      if (!nav.contains(e.target)) setMega(false);
    });

    desktop.addEventListener('change', () => {
      if (!desktop.matches) setMega(false);
    });
  }

  // Uitklapbare zoekbalk: klik op het icoon opent het veld (geen navigatie),
  // nogmaals klikken met een lege zoekterm sluit; Escape en klik buiten ook.
  function initZoek(nav) {
    const knop = nav.querySelector('.navbar__zoekknop');
    const vorm = nav.querySelector('.navbar__zoekform') || nav.querySelector('.navbar__zoekvorm');
    const veld = nav.querySelector('.navbar__zoekveld');
    if (!knop || !vorm || !veld) return;

    const setZoek = (open) => {
      nav.classList.toggle('navbar--zoek-open', open);
      knop.setAttribute('aria-expanded', String(open));
      if (open) {
        setTimeout(() => veld.focus(), 100);
      }
    };

    knop.addEventListener('click', () => {
      const open = nav.classList.contains('navbar--zoek-open');
      if (open && veld.value.trim() !== '') {
        vorm.submit();
        return;
      }
      setZoek(!open);
    });

    veld.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        setZoek(false);
        knop.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!nav.classList.contains('navbar--zoek-open')) return;
      if (!e.target.closest('.navbar__zoek')) setZoek(false);
    });
  }

  // Entrance: navcluster valt van boven in met stagger, parallel aan de
  // hero-timeline zodat de paginareveal gecoördineerd voelt.
  function initEntrance(nav) {
    const gsap = window.gsap;
    if (typeof gsap === 'undefined') return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const candidates = nav.querySelectorAll(
      '.navbar__brand, .navbar__link, .navbar__icoon, .navbar__cta, .navbar__toggle'
    );
    const items = Array.from(candidates).filter(
      (el) => getComputedStyle(el).display !== 'none'
    );
    if (!items.length) return;

    if (prefersReduced) {
      gsap.set(items, { autoAlpha: 1, y: 0 });
      return;
    }

    gsap.set(items, { autoAlpha: 0, y: -12 });
    gsap.to(items, {
      autoAlpha: 1,
      y: 0,
      duration: 0.6,
      ease: 'power3.out',
      stagger: 0.07,
      delay: 0.05,
      onComplete: () => {
        // Inline transforms opruimen zodat de CSS-hover met `scale` weer kan
        // animeren (CSS kan niet interpoleren vanaf `none`).
        gsap.set(items, { clearProps: 'translate,rotate,scale,transform,y' });
      },
    });
  }

  function init() {
    const nav = document.querySelector('.navbar');
    if (!nav || nav.dataset.ivyInit === 'true') return;
    nav.dataset.ivyInit = 'true';
    updateTransparant(nav);
    initPaneel(nav);
    initScrollGedrag(nav);
    initMega(nav);
    initZoek(nav);
    initEntrance(nav);
  }

  document.addEventListener('DOMContentLoaded', init);
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
